import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { type GallerySectionAddition } from '@air/component-gallery-view';
import { useBreakpointsContext } from '@air/provider-media-query';

import { type GalleryMetadata } from '~/components/Gallery/galleryMetadata';
import { type GalleryItemType } from '~/components/Gallery/types';
import { ListSectionHeader } from '~/components/Shared/ListSectionHeader';
import {
  WKSPC_GALLERY_FIRST_SECTION_GAP_DESKTOP,
  WKSPC_GALLERY_FIRST_SECTION_GAP_MOBILE,
} from '~/constants/WorkspaceSpacing';

export const SECTIONS_DISTANCE = 64;
const SECTION_HEADER_EXTRA_HEIGHT = 16;
const NON_FIRST_SECTION_SPACING = 24;

export type UseGallerySectionHeaderParams = {
  isFirstSection: boolean;
  containerHeight?: number;
  containerWidth: number;
  label: string;
  sectionType: GalleryItemType;
  itemCount?: number;
  containerPadding: number;
  onSectionCollapse?: (isCollapsed: boolean) => void;
  renderHeader?: ({
    collapsed,
    onToggleCollapse,
    label,
    sectionType,
    itemCount,
    isFirstSection,
  }: {
    collapsed: boolean;
    onToggleCollapse: () => void;
    label: string;
    sectionType: GalleryItemType;
    itemCount?: number;
    isFirstSection: boolean;
  }) => ReactNode;
};

export const useGallerySectionHeader = ({
  isFirstSection,
  containerHeight,
  containerWidth,
  label,
  sectionType,
  itemCount,
  containerPadding,
  onSectionCollapse,
  renderHeader,
}: UseGallerySectionHeaderParams): { isHeaderCollapsed: boolean; header: GallerySectionAddition<GalleryMetadata> } => {
  const [isHeaderCollapsed, setHeaderCollapsed] = useState(false);
  const { isAboveMediumScreen } = useBreakpointsContext();

  const headerBox = useMemo(() => {
    const getDefaultBaseHeight = () => {
      if (!isFirstSection) {
        return SECTIONS_DISTANCE + SECTION_HEADER_EXTRA_HEIGHT;
      }

      return !isAboveMediumScreen ? WKSPC_GALLERY_FIRST_SECTION_GAP_MOBILE : WKSPC_GALLERY_FIRST_SECTION_GAP_DESKTOP;
    };
    const baseHeight = containerHeight ?? getDefaultBaseHeight();

    const spacing = !isFirstSection ? NON_FIRST_SECTION_SPACING : 0;

    return {
      aspectRatio: 0,
      left: containerPadding,
      top: 0,
      width: containerWidth - containerPadding,
      height: baseHeight + spacing,
    };
  }, [containerPadding, containerWidth, isFirstSection, isAboveMediumScreen, containerHeight]);

  const onToggleCollapse = useCallback(() => {
    setHeaderCollapsed((collapsed) => {
      onSectionCollapse?.(!collapsed);
      return !collapsed;
    });
  }, [onSectionCollapse]);

  const render = useCallback(() => {
    if (renderHeader) {
      return renderHeader({
        collapsed: isHeaderCollapsed,
        onToggleCollapse,
        label,
        sectionType,
        itemCount,
        isFirstSection,
      });
    }

    return (
      <ListSectionHeader
        className={!isFirstSection ? 'pt-6' : ''}
        headerTitle={`${label} (${itemCount?.toLocaleString()})`}
        collapsed={isHeaderCollapsed}
        onClick={onToggleCollapse}
      />
    );
  }, [isHeaderCollapsed, onToggleCollapse, renderHeader, label, sectionType, itemCount, isFirstSection]);

  return {
    isHeaderCollapsed,
    header: {
      box: headerBox,
      render,
    },
  };
};
